/*
 * Extreme Values
 */
import {Button, Checkbox, Col, DatePicker, Layout, message, Row, Space, Spin, Typography} from 'antd';
import { Select } from 'antd';
import React, {useEffect, useState, useCallback, useMemo, ReactNode, useRef} from 'react';
import { isEmpty } from 'underscore';
import { IPlotVariable } from '../../../../typings/analytics';
import styles from '../../Style/LayoutPanel.module.scss';
import { ChartBox } from '../ChartBox';
import { IBestDataset } from '../../../../typings/analytics';
import { IAdvancedAnalyticsApiPayload } from '../../../../typings/chart';
import { IEnrichedMetadata } from '../../../../typings/metadata';
import { ADVANCED_ANALYTICS } from '../AnalyticsPanel';
import moment, { Moment } from 'moment';
import { DateFormat, DT0Format } from '../../../../generic/StaticVariable';
import { IAnalytcisUserInput } from '../../../../typings/IAnalyticsUserInput';
import IconPoint from '../../../../assets/icon/locate2.png'
import CustomDivider from '../CustomDIvider';
import { MarkerItem } from '../../../../typings/home';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export default function ExtremeValues(props: {
  coordinates: number[];
  previewDatasetId: string;
  datasets: IBestDataset[];
  onDatasetChange: (item: string) => void
  dateRange: [moment.Moment, moment.Moment];
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  extremeValuesImage: string;
  extremeValuesLinks: string[];
  error: string;
  updating: boolean;
  pointName: string;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  point?: MarkerItem
  listNavigation: ReactNode
  onChangeActiveKey: (item: any) => void
  pointOption: { label: string, value: string }[]
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean
  setIsForAllAnalytics: (item: any) => void
}) {
  const {
    datasets,
    dateRange,
    previewDatasetId,
    coordinates,
    enrichedMetadata,
    onUpdate,
    extremeValuesImage,
    extremeValuesLinks,
    error,
    pointName,
    userInput,
    updating,
    updateUserInput,
    onDatasetChange,
    point,
    listNavigation,
    onChangeActiveKey,
    pointOption,
    globalDateRange,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
  } = props;

  const [variablesX, setVariablesX] = useState<IPlotVariable[]>([]);
  const [dataset, setDataset] = useState(userInput.ExtremeValues.dataset);
  const [variableX, setVariableX] = useState<IPlotVariable>();
  const [variableXIndex, setVariableXIndex] = useState(
    userInput.ExtremeValues.variable
  );
  const [startDate, setStartDate] = useState(
    userInput.ExtremeValues.startDate ||
    moment(dateRange[0])
  );
  const [endDate, setEndDate] = useState(
    userInput.ExtremeValues.endDate || moment(dateRange[1])
  );

  const selfTriggered = useRef(false);
  const preventRun = useRef(false);

  useEffect(() => {
    updateUserInput({
      ExtremeValues: {
        dataset: dataset,
        variable: variableXIndex,
        startDate: startDate,
        endDate: endDate,
      },
    });
  }, [dataset, variableXIndex, startDate, endDate]);

  const getImage = useCallback(
    (
      indexX: IPlotVariable,
      orderId: string,
      startDate: string,
      endDate: string
    ) => {
      const [longitude, latitude] = coordinates;
      if (indexX) {
        const payload: IAdvancedAnalyticsApiPayload = {
          PointName: pointName,
          OrderId: orderId,
          OriginalOrderId: orderId,
          Longitude: longitude,
          Latitude: latitude,
          StartDate: startDate,
          EndDate: endDate,
          Title: indexX.Name,
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          ColumnIndexX: indexX.Index,
          IntervalCountX: 16,
          AnalyticType: ADVANCED_ANALYTICS.EXTREME_VALUES,
          DatasetId: previewDatasetId,
          Thresholds: '',
          Duration: '1',
          Percentile: 50,
          Sign1: 'below',
          OriginalDatasetId: previewDatasetId,
          enableConditional: false,
          OrderId2: orderId,
          DatasetId2: previewDatasetId,
          AcronymY: indexX.ShortName ? indexX.ShortName : indexX.Name,
          TitleY: indexX.Name,
          ColumnIndexY: indexX.Index,
          Thresholds2: '',
          Sign2: 'below',
          IntervalCountY: 16,
        };
        onUpdate(payload);
      }
    },
    [coordinates, onUpdate, previewDatasetId, pointName]
  );


  useEffect(() => {
    if (enrichedMetadata) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === previewDatasetId &&
          task.CoordinatesString === props.coordinates.join()
      );
      if (task) {
        const allowedItems = task.ExtremeValueDefaultItems.split('|');
        const itemsInfos = task.ItemsInfo.filter((itemInfo: IPlotVariable) =>
          itemInfo.ShortName
            ? allowedItems.includes(itemInfo.ShortName)
            : allowedItems.includes(itemInfo.Name)
        );
        if (itemsInfos.length > 0) {
          const wlItem = itemsInfos.find((item) => item.ShortName === 'WL');
          let indexX
          if (wlItem !== undefined) {
            // Backend requires that WL is separated into 2 items.
            const withoutWL = itemsInfos.filter(
              (item) => item.ShortName !== 'WL'
            );
            const modifieditems = withoutWL.concat([
              {
                ...wlItem,
                ShortName: 'WL-positive',
                Name: 'Water Level - High (positive)',
              },
              {
                ...wlItem,
                ShortName: 'WL-negative',
                Name: 'Water Level - High (negative)',
              },
            ]);
            setVariablesX(modifieditems);
            indexX = modifieditems[0];
          } else {
            setVariablesX(itemsInfos);
            indexX = itemsInfos[0];
          }
          if (previewDatasetId && !isEmpty(datasets) && !isEmpty(coordinates) && extremeValuesImage === '') {
            setDataset(previewDatasetId);
            let dateRange = [startDate, endDate]
            setVariableX(indexX);
            if(isForAllAnalytics){
              dateRange = globalDateRange
              setStartDate(dateRange[0])
              setEndDate(dateRange[1])
            }
            selfTriggered.current = true
            preventRun.current = true
            getImage(
              indexX,
              task.OrderId,
              dateRange[0].format(DT0Format),
              dateRange[1].format(DT0Format)
            );
          }
        }
        }
    }
  }, [previewDatasetId, datasets, enrichedMetadata, coordinates, extremeValuesImage]);

  const onUpdateExtremeValues = useCallback(
    (dateRange?: any) => {
      let variable = variableX
      if(!variable && variableXIndex !== undefined && variableXIndex !== null){
        variable = variablesX[variableXIndex]
      }
      if (enrichedMetadata && variable) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === dataset &&
            task.CoordinatesString === props.coordinates.join()
        )

        if (task) {
          getImage(
            variable,
            task.OrderId,
            dateRange? dateRange[0].format(DT0Format) : startDate.format(DT0Format),
            dateRange? dateRange[1].format(DT0Format) : endDate.format(DT0Format),
          );
        }
      }
    }
    ,
    [enrichedMetadata, dataset, startDate, endDate, variableX, variableXIndex, variablesX, pointName]
  );

  useEffect(() => {
    if(isForAllAnalytics && !isEmpty(variablesX) && extremeValuesImage !== '' && !preventRun.current ){
      if (selfTriggered.current) {
        selfTriggered.current = false;
        return;
      }
      if(globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdateExtremeValues(globalDateRange)
      }
      setStartDate(globalDateRange[0])
      setEndDate(globalDateRange[1])
    }
  }, [isForAllAnalytics, extremeValuesImage, variablesX])

  function doVariableChange(active: number) {
    selfTriggered.current = true
    const item = variablesX[active]; //.find((a) => a.Index === active)!;
    setVariableX(item);
    setVariableXIndex(active);
  }

  const doStartDateChange = (value: any) => {
    if (value) {
      setStartDate(value)
      if(isForAllAnalytics){
        setGlobalDateRange([value, endDate])
      }
    }
  };

  const doEndDateChange = (value: any) => {
    if (value) {
      setEndDate(value);
      if(isForAllAnalytics){
        setGlobalDateRange([startDate, value])
      }
    }
  };

  const onSelectDataset = (value: string) => {
    onDatasetChange(value)
  }

  function disabledStartDate(current: Moment) {
    // Can not select date after end date
    const isAfterEndDate = current && current > endDate;
    // and not before preview dataset's time range and not after preview dataset's time range
    return isAfterEndDate
      ? isAfterEndDate
      : current.isBefore(dateRange[0], 'day');
  }

  function disabledEndDate(current: Moment) {
    // Can not select date before start date and not after preview dataset's time range
    const isBeforeStartDate = current && current < startDate;
    return isBeforeStartDate
      ? isBeforeStartDate
      : current.isAfter(dateRange[1], 'day');
  }

  const categories = useMemo(() => {
    const label = datasets.map((bd: IBestDataset) => {
      return bd.label;
    })

    return Array.from(new Set(label));
  }, [datasets])

  const handleCheckboxChange = () => {
    setGlobalDateRange([startDate, endDate])
    setIsForAllAnalytics(!isForAllAnalytics);
    selfTriggered.current = true
  };

  return (
    <Layout style={{minWidth: 1200, height: '100%', overflow: 'hidden', background: 'white'}}>
      <Header className={styles.header}>
        <Row justify="space-between" align="middle" className={styles.headerRow}>
          <Col>
            <Row gutter={[0, 8]} align="bottom">
              <Col className={styles.colPoint}>
              <Space>
                <Text>Point</Text>
                <img src={IconPoint} width={20} height={20} />
              </Space>
              <Select
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="Select point"
                onChange={onChangeActiveKey}
                options={pointOption}
                value={point}
              />
              </Col>
              <Col span={24} md={4} className={styles.colItem}>
                <Text>Dataset</Text>
                <br />
                <Select
                  value={dataset}
                  style={{ width: '100%', marginTop: '4px' }}
                  onChange={onSelectDataset}
                  disabled={updating}
                >
                  {categories.map((category: string) => (
                    <Select.OptGroup key={category} label={category}>
                      {datasets
                        .filter((item) => item.label === category)
                        .map((item) => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.value}
                          </Select.Option>
                        ))}
                    </Select.OptGroup>
                  ))}
                </Select>
              </Col>
              <Col span={24} md={4} className={styles.colItem}>
                <Text>Variable</Text>
                <br />
                <Select
                  style={{ width: '100%', marginTop: '4px' }}
                  placeholder="Select variable"
                  onChange={doVariableChange}
                  value={variableXIndex}
                  disabled={updating}
                >
                 {variablesX.map((item, id) => {
                    return (
                      <Select.Option key={id} value={id}>
                        {item.Name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>Start Date</Text>
                <DatePicker
                  value={moment(startDate)}
                  disabledDate={disabledStartDate}
                  onChange={doStartDateChange}
                  format={DateFormat}
                  disabled={updating}
                  className={styles.datePicker}
                />
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>End Date</Text>
                <DatePicker
                  value={moment(endDate)}
                  disabledDate={disabledEndDate}
                  onChange={doEndDateChange}
                  format={DateFormat}
                  disabled={updating}
                  className={styles.datePicker}
                />
              </Col>
              <Col span={24} md={5} className={styles.checkboxItem} >
                <Checkbox checked={isForAllAnalytics} onClick={handleCheckboxChange} />
                <Text onClick={handleCheckboxChange} style={{cursor: 'pointer'}}>Use this period for all analytics</Text>
              </Col>
            </Row>
          </Col>
          <Col style={{width: 201}}>
          <div style={{height: '60px', padding: 20, borderLeft: '2px solid #f0f0f0'}}>
            <Button
              type="primary"
              disabled={updating}
              loading={updating}
              onClick={() => onUpdateExtremeValues()}
              style={{ width: 100, bottom: 10}}
            >
              Update
            </Button>
          </div>
          </Col>
        </Row>
      </Header>
      <CustomDivider  />
      <Layout>
        <Sider theme="light">
          {listNavigation}
        </Sider>
        <Content style={{ padding: '10px', minHeight: '400px', borderLeft: '2px solid #f0f0f0', borderRight: '2px solid #f0f0f0', background: 'white'}}>
        {!updating && error && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>{error}</div>}
        {!updating && !error && (
          <ChartBox
            actionRow={<></>}
            key={'Extreme Values'}
            title={'Extreme Values'}
            height={updating || !isEmpty(error) ? '50px' : 'auto'}
            plot={
              updating ? (
                <div className={styles.spinner}>
                  <Spin size="large" />
                </div>
              ) : !isEmpty(error) ? (
                <div>{error}</div>
              ) : extremeValuesImage && (
                <img
                  style={{ width: '80%', objectFit: 'scale-down', maxHeight: '65vh' }}
                  src={extremeValuesImage}
                  alt="ExtremeValues"
                />
              )
            }
            dataset={''}
            showDisclaimer
            link={extremeValuesLinks}
          />
          )}
          {updating && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
              <Spin style={{margin: 'auto'}} size='large' />
            </div>
          )}
        </Content>
        <Sider
          width={200}
          className={styles.siderChildren}
          theme="light"
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        />
      </Layout>
    </Layout>
  );
}
